import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Group, WorkSpace } from '../../core/types';
import { Project } from '../../core/objects/project';
import { CountriesService } from '../../core/services/countries.service';
import { ProjectService } from '../../core/services/project.service';
import { ColorPipe } from '../../shared/pipes/color.pipe';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  validateFirstAndLastOperationsTypes,
  validateOperationHours,
  ValidateOperationDates,
  ValidateFirstOperationDate,
} from './transFormValidators';
import { allowedCountryCodes, currencies, requiredTrackBodies, vehicleSizes } from './transValues';

interface FormConfig {
  publish: boolean[];
  capacity: [number, (Validators | ValidatorFn)[]];
  vehicle_size: [string[], (Validators | ValidatorFn)[]];
  ldm: [number];
  requirements: UntypedFormGroup;
  spots: UntypedFormArray;
  payment?: UntypedFormGroup;
  monitoring?: UntypedFormGroup;
}

@Component({
    selector: 'gl-trans-form',
    templateUrl: './trans-form.component.html',
    styleUrls: ['./trans-form.component.scss'],
    standalone: false
})
export class TransFormComponent implements OnInit {
  public freightForm: UntypedFormGroup;
  public countries: { name: string; code: string }[] = [];
  public checkboxes: boolean[] = [];
  public vehiclesCheckboxes: boolean[] = [];
  public loadsGroups: Group[] = [];
  public currencies = currencies;
  public requiredTrackBodies = requiredTrackBodies;
  public vehicleSizes = vehicleSizes;
  public today: string;
  public isMultiStop = false;
  private project: Project;
  public isDisabled = true;
  @Input() public isOrder: boolean;
  @Output() public submitForm: EventEmitter<UntypedFormGroup> = new EventEmitter<UntypedFormGroup>();

  constructor(
    private fb: UntypedFormBuilder,
    private colorPipe: ColorPipe,
    private translate: TranslateService,
    private projectService: ProjectService,
    private countriesService: CountriesService,
  ) {
    this.currentLang = this.translate.currentLang;
  }

  private currentLang: string;

  public get spots(): UntypedFormArray {
    return this.freightForm?.get('spots')
      ? (this.freightForm.get('spots') as UntypedFormArray)
      : this.fb.array([]);
  }

  private translateList(
    list: { label: string; value: string }[],
  ): { label: string; value: string }[] {
    return list.map((item) => ({
      label: this.translate.instant(item.label),
      value: item.value,
    }));
  }

  public getColor(group: Group): string {
    return this.colorPipe.transform(group.color);
  }

  public getOperationControls(spot: AbstractControl): AbstractControl[] {
    return (spot?.get('operations') as UntypedFormArray)?.controls;
  }

  public getLoadControls(operation: AbstractControl): any[] {
    return (operation?.get('loads') as UntypedFormArray)?.controls;
  }

  public getLoadsSelectControl(operation: AbstractControl): UntypedFormControl {
    return operation?.get('loadsSelect') as UntypedFormControl;
  }

  public onSearch(value: string): void {
    this.countries = this.countriesService.getCountriesWithCodes(this.currentLang, allowedCountryCodes);
    if (value) {
      this.countries = this.countries.filter((country) =>
        country.name.toLowerCase().includes(value.toLowerCase()),
      );
    }
  }

  public setVehicleSize(size: { label: string; value: string }): void {
    const vehicleSizeIndex = this.vehicleSizes.findIndex((s) => s.value === size.value);
    if (vehicleSizeIndex !== -1) {
      this.vehiclesCheckboxes[vehicleSizeIndex] = !this.vehiclesCheckboxes[vehicleSizeIndex];
    }

    this.freightForm.get('vehicle_size').setValue(this.getFirstCheckedVehicle());
  }

  public getFirstCheckedBody(): string {
    const index = this.checkboxes.findIndex((checkbox) => checkbox);
    return index !== -1 ? this.requiredTrackBodies[index].value : null;
  }

  public getFirstCheckedVehicle(): string {
    const index = this.vehiclesCheckboxes.findIndex((checkbox) => checkbox);
    return index !== -1 ? this.vehicleSizes[index].value : null;
  }

  public setRequiredTrucks(body: { label: string; value: string }): void {
    const requiredTruckBodiesIndex = this.requiredTrackBodies.findIndex(
      (b) => b.value === body.value,
    );
    if (requiredTruckBodiesIndex !== -1) {
      this.checkboxes[requiredTruckBodiesIndex] = !this.checkboxes[requiredTruckBodiesIndex];
    }

    this.freightForm.get('requirements.required_truck_bodies').setValue(this.getFirstCheckedBody());
  }

  ngOnInit(): void {
    const today = new Date();
    today.setMinutes(today.getMinutes() + 30);
    this.today = today.toISOString().slice(0, 16);
    this.generateCountries();
    this.checkboxes = new Array(requiredTrackBodies.length).fill(false);
    this.vehiclesCheckboxes = new Array(vehicleSizes.length).fill(false);
    this.currencies = this.translateList(currencies);
    this.requiredTrackBodies = this.translateList(requiredTrackBodies);
    this.vehicleSizes = this.translateList(vehicleSizes);
    this.getLoads().subscribe(() => {
      this.createForm();
    });
  }

  public generateCountries(): void {
    this.countries = this.countriesService.getCountriesWithCodes(this.currentLang, allowedCountryCodes);
  }

  private getProjectCapacity(): number {
    const workSpace: WorkSpace = this.project.workSpaces[this.projectService.getCurrentWorkSpace()];
    const isSemiTrailer: boolean = workSpace?.cargoSpaces?.length > 1;
    const workSpaceCapacity: number = isSemiTrailer
      ? workSpace?.cargoSpaces[0]?.limit + workSpace?.cargoSpaces[1]?.limit
      : workSpace?.cargoSpaces[0]?.limit;
    return workSpaceCapacity / 1000;
  }

  private getProjectLdm(): number {
    const workSpace: WorkSpace = this.project.workSpaces[this.projectService.getCurrentWorkSpace()];
    const isSemiTrailer: boolean = workSpace?.cargoSpaces?.length > 1;
    const workSpaceCapacity: number = isSemiTrailer
      ? workSpace?.cargoSpaces[0]?.ldm + workSpace?.cargoSpaces[1]?.ldm
      : workSpace?.cargoSpaces[0]?.ldm;
    return workSpaceCapacity / 100;
  }

  private getVehicleSize(): string[] {
    const workSpace: WorkSpace = this.project.workSpaces[this.projectService.getCurrentWorkSpace()];
    const isSemiTrailer: boolean = workSpace?.cargoSpaces?.length > 1;
    const capacity = this.getProjectCapacity();
    let vehicleSize: string = capacity <= 3.5 ? 'bus' : 'solo';
    vehicleSize = isSemiTrailer ? 'double_trailer' : vehicleSize;
    this.vehiclesCheckboxes[vehicleSizes.findIndex((size) => size.value === vehicleSize)] = true;
    return [vehicleSize];
  }

  private getRequiredTruckBodies(): string[] {
    const workSpace: WorkSpace = this.project.workSpaces[this.projectService.getCurrentWorkSpace()];
    const capacity = this.getProjectCapacity();
    const isCooler = workSpace?.cargoSpaces[0]?.extraFeatures?.coolerTrailerLength !== null;
    let requiredTruckBody: string = capacity >= 12 ? 'curtainsider' : 'curtainsider';
    requiredTruckBody = isCooler ? 'cooler' : requiredTruckBody;
    this.checkboxes[requiredTrackBodies.findIndex((body) => body.value === requiredTruckBody)] =
      true;
    return [requiredTruckBody];
  }

  private createForm(): void {
    const spots: UntypedFormArray = this.fb.array([]);
    if (this.isMultiStop) {
      const stops: WorkSpace[] = this.project.workSpaces[0]?.stops;
      stops.forEach((_, index: number): void => {
        const spot: UntypedFormGroup = this.createSpotForm(index + 1);
        if (spot.value.operations.length !== 0) {
          spots.push(spot);
        }
      });
    } else {
      spots.push(this.createSpotForm(1, 'loading'));
      spots.push(this.createSpotForm(2, 'unloading'));
    }
    const formConfig: FormConfig = {
      publish: [true],
      capacity: [this.getProjectCapacity(), [Validators.required, Validators.min(0)]],
      vehicle_size: [this.getVehicleSize(), []],
      ldm: [this.getProjectLdm()],
      requirements: this.fb.group({
        is_ftl: [false],
        required_truck_bodies: [this.getRequiredTruckBodies(), Validators.required],
      }),
      spots: spots,
    };

    if (this.isOrder) {
      formConfig.payment = this.fb.group({
        days: [0, [Validators.required, Validators.min(1)]],
        price: this.fb.group({
          type: [null, Validators.required],
          value: [0, [Validators.required, Validators.min(0)]],
          currency: [null, Validators.required],
        }),
      });
      formConfig.monitoring = this.fb.group({
        required: [false],
      });
    }
    this.freightForm = this.fb.group(formConfig, {
      validators: [
        ValidateOperationDates(),
        ValidateFirstOperationDate(),
        validateFirstAndLastOperationsTypes(),
      ],
    });
  }

  private getLoads(): Observable<any> {
    return this.projectService.project.pipe(
      tap((project): void => {
        this.project = project;
        this.isMultiStop = project.workSpaces[0]?.stops.length > 1;
        project.workSpaces[
          this.projectService.getCurrentWorkSpace()
        ]?.cargoSpaces[0]?.groups.forEach((group: Group): void => {
          this.loadsGroups.push(group);
        });
        if (project.workSpaces[this.projectService.getCurrentWorkSpace()]?.cargoSpaces[1]) {
          project.workSpaces[
            this.projectService.getCurrentWorkSpace()
          ].cargoSpaces[1].groups.forEach((group: Group): void => {
            this.loadsGroups.push(group);
          });
        }
      }),
    );
  }

  private getCountryCode(name: string): string {
    return this.countries.find((country) => country.name === name)?.code;
  }

  private createPlaceForm(spotOrder = null): UntypedFormGroup {
    if (!this.isMultiStop) {
      return this.fb.group({
        address: this.fb.group({
          country: [null, Validators.required],
          locality: [null, Validators.required],
          postal_code: [null, Validators.required],
        }),
      });
    } else {
      const currentStop: WorkSpace = this.project.workSpaces[0].stops[spotOrder - 1];
      return this.fb.group({
        address: this.fb.group({
          country: [this.getCountryCode(currentStop.country), Validators.required],
          locality: [currentStop.city, Validators.required],
          postal_code: [currentStop.postalCode, Validators.required],
        }),
      });
    }
  }

  private getAllGroupsFromPreviousStops(spotOrder: number): Group[] {
    const groups: Group[] = [];
    for (let i = 0; i < spotOrder - 1; i++) {
      const stop: WorkSpace = this.project.workSpaces[0].stops[i];
      const trailerLoadingSpace: Group[] = stop.cargoSpaces[0].groups;
      const semiTrailerLoadingSpace: Group[] =
        stop.cargoSpaces.length > 1 ? stop.cargoSpaces[1]?.groups : [];
      const loadingSpace: Group[] = [...trailerLoadingSpace, ...semiTrailerLoadingSpace];
      groups.push(...loadingSpace);
    }
    return groups;
  }

  private createLoadsFormArray(spotOrder: number, kind = null): UntypedFormArray {
    if (this.isMultiStop) {
      const fbArray: UntypedFormArray = this.fb.array([]);
      const stop: WorkSpace = this.project.workSpaces[0].stops[spotOrder - 1];
      const trailerLoadingSpace: Group[] = stop.cargoSpaces[0].groups;
      const semiTrailerLoadingSpace: Group[] =
        stop.cargoSpaces.length > 1 ? stop.cargoSpaces[1]?.groups : [];
      const allGroupsFromPreviousStops: Group[] = this.getAllGroupsFromPreviousStops(spotOrder);
      const loadingSpace: Group[] = [...trailerLoadingSpace, ...semiTrailerLoadingSpace].filter(
        (group) => !allGroupsFromPreviousStops.find((gr) => gr.id === group.id),
      );
      const unloadingSpace: Group[] = stop.unloadingSpace?.groups;
      const loadGroups: Group[] = kind === 'loading' ? loadingSpace : unloadingSpace;
      loadGroups.forEach((group: Group): void => {
        fbArray.push(this.createLoadForm(group, group.quantity));
      });
      return fbArray;
    } else {
      const fbArray: UntypedFormArray = this.fb.array([]);
      this.loadsGroups.forEach((group: Group): void => {
        fbArray.push(this.createLoadForm(group, group.quantity));
      });
      return fbArray;
    }
  }

  private getFirstLoadForOperation(loads: UntypedFormArray): string {
    const firstLoadControl: AbstractControl = loads.controls.find(
      (control) => control.get('isChecked')?.value,
    );
    return firstLoadControl ? (firstLoadControl.value.load.id as string) : null;
  }

  private createOperationForm(spotOrder: number, type: string): UntypedFormGroup {
    const loads: UntypedFormArray = this.createLoadsFormArray(spotOrder, type);
    return this.fb.group({
      type: [type, Validators.required],
      loadsSelect: [this.getFirstLoadForOperation(loads)],
      loads: loads,
      timespans: this.fb.group(
        {
          begin: [null, Validators.required],
          end: [null],
        },
        { validators: validateOperationHours() },
      ),
    });
  }

  public preventClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  private createLoadForm(load = null, numberOfLoads = 0): UntypedFormGroup {
    return this.fb.group({
      load: [load],
      numberOfLoads: [numberOfLoads],
      isChecked: [true],
    });
  }

  private filterPreviousLoads(spotOrder: number, loads: Group[]): Group[] {
    const allGroupsFromPreviousStops: Group[] = this.getAllGroupsFromPreviousStops(spotOrder);
    return loads.filter((group) => !allGroupsFromPreviousStops.find((gr) => gr.id === group.id));
  }

  private createSpotForm(spotOrder: number, operationType: string = null): UntypedFormGroup {
    const operationsArray: UntypedFormArray = this.fb.array([]);
    if (this.isMultiStop) {
      const stop: WorkSpace = this.project.workSpaces[0].stops[spotOrder - 1];
      const isLoading: boolean =
        this.filterPreviousLoads(spotOrder, stop.cargoSpaces[0].groups).length > 0;
      const isUnloading: boolean = stop.unloadingSpace.groups.length > 0;
      const isLastSpot: boolean = spotOrder === this.project.workSpaces[0].stops.length;
      if (isLoading) {
        operationsArray.push(this.createOperationForm(spotOrder, 'loading'));
      }
      if (isUnloading && !isLastSpot) {
        operationsArray.push(this.createOperationForm(spotOrder, 'unloading'));
      }
      if (isLastSpot) {
        operationsArray.push(this.createOperationForm(spotOrder, 'unloading'));
      }
    } else {
      operationsArray.push(this.createOperationForm(spotOrder, operationType));
    }
    return this.fb.group({
      spot_order: [spotOrder],
      place: this.createPlaceForm(spotOrder),
      operations: operationsArray,
    });
  }

  private prepareAndSetVehiclesSize(): string[] {
    return this.vehicleSizes
      .filter((_, index) => this.vehiclesCheckboxes[index])
      .map((size) => size.value);
  }

  private prepareAndSetRequiredTruckBodies(): string[] {
    return this.requiredTrackBodies
      .filter((_, index) => this.checkboxes[index])
      .map((body) => body.value);
  }

  private changeFreightForm(): void {
    this.freightForm.get('vehicle_size').setValue(this.prepareAndSetVehiclesSize());
    this.freightForm
      .get('requirements.required_truck_bodies')
      .setValue(this.prepareAndSetRequiredTruckBodies());
  }

  public onSubmit(): void {
    this.freightForm.updateValueAndValidity();
    if (this.freightForm.valid) {
      this.changeFreightForm();
      this.submitForm.emit(this.freightForm.value);
    }
  }
}
